import React, { useState, useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import OrderBoxIcon from "../../../assets/svg/orderbox.svg";
import search from "../../../assets/svg/search.svg";
import FillStartIcon from "../../../assets/svg/fill-star.svg";
import StartIcon from "../../../assets/svg/star.svg";
import ProductImage from "../../../assets/images/our-product.png";
import { GetAllOrder, GenerateInvoice, UpdateOrderStatus } from "../../../Services/OrderService";
import { AddReviewOnProduct } from "../../../Services/ProductService";
import { getImageUrl, handleImageError } from "../../../utils/imageUtils";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("Past 3 Months");
  const [loading, setLoading] = useState(true);
  const [loadingState, setLoadingState] = useState({
    download: null,
    reviewing: null,
    cancelOrder: null,
    makePayment: null,
  });
  // ⭐⭐⭐ MODAL STATE ⭐⭐⭐
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");

  const navigate = useNavigate();
  const modalRef = useRef();
  
  // Show modal
  const openModal = (orderId, productId, rating) => {
    setSelectedOrderId(orderId);
    setSelectedProductId(productId);
    setSelectedRating(rating);
    setComment("");

    const modal = new window.bootstrap.Modal(modalRef.current);
    modal.show();
  };

  // Hide modal
  const closeModal = () => {
    const modal = window.bootstrap.Modal.getInstance(modalRef.current);
    modal.hide();
  };

  // ⭐⭐⭐ SUBMIT REVIEW (Rating + Comment) ⭐⭐⭐
  const submitReview = async () => {
    if (!comment.trim()) {
      alert("Please write a comment");
      return;
    }

    const userId = localStorage.getItem("userId");

    const reviewData = {
      OrderId: selectedOrderId,
      ProductId: selectedProductId,
      UserId: parseInt(userId),
      Rating: selectedRating,
      Comment: comment,
    };

    try {
      await AddReviewOnProduct(reviewData);
      closeModal();
      fetchOrders(searchTerm); // refresh UI
    } catch (err) {
      console.error("Error submitting review:", err);
    }
  };

  // Date filter options
  const currentYearVal = new Date().getFullYear();
  const dateFilterOptions = [
    "Past 3 Months",
    currentYearVal.toString(),
    (currentYearVal - 1).toString(),
    (currentYearVal - 2).toString(),
    (currentYearVal - 3).toString()
  ];

  // Get start and end dates based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    let startDate, endDate;

    if (filter === "Past 3 Months") {
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      endDate = now;
    } else {
      // Assume it's a year like "2025"
      const year = parseInt(filter);
      startDate = new Date(year, 0, 1); // Jan 1
      endDate = new Date(year, 11, 31); // Dec 31
    }

    return {
      startDate: startDate.toLocaleDateString('en-CA'), // YYYY-MM-DD format
      endDate: endDate.toLocaleDateString('en-CA'),
    };
  };

  // Fetch orders
  const fetchOrders = async (term = "", filter = dateFilter) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      console.error("User ID not found");
      setLoading(false);
      return;
    }
    try {
      const params = { userId: parseInt(userId) };
      if (term) params.filter = term;
      const { startDate, endDate } = getDateRange(filter);
      params.startDate = startDate;
      params.endDate = endDate;
      const response = await GetAllOrder(params);
      setOrders(response?.data?.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle date filter change
  const handleDateFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchOrders(searchTerm);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrders(searchTerm, dateFilter);
  }, [dateFilter]);

  // Download Invoice
  const handleDownloadInvoice = async (orderId) => {
    setLoadingState((prev) => ({ ...prev, download: orderId }));
    try {
      const response = await GenerateInvoice(orderId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setLoadingState((prev) => ({ ...prev, download: null }));
    }
  };

  // Cancel order
  const handleCancelOrder = async (orderId) => {
    setLoadingState((prev) => ({ ...prev, cancelOrder: orderId }));
    try {
      const res = await UpdateOrderStatus(orderId, 6);
      if (res?.status) {
        fetchOrders();
      } else {
        console.error(`Failed to update status for order ID: ${orderId}`);
      }
    } finally {
      setLoadingState((prev) => ({ ...prev, cancelOrder: null }));
    }
  };

  // Redirect to Make Payment
  const handleMakePayment = (order) => {
    if (loadingState.makePayment) return;
    setLoadingState((prev) => ({ ...prev, makePayment: order.orderId }));

    navigate(`/order-payment`, { state: { order: order } });

    setTimeout(() => {
      setLoadingState((prev) => ({ ...prev, makePayment: null }));
    }, 500);
  };

  // Add Review
  const handleStarClick = async (orderId, productId, rating) => {
    setLoadingState((prev) => ({ ...prev, reviewing: productId }));
    try {
      const userId = localStorage.getItem("userId");
      const reviewData = {
        OrderId: orderId,
        ProductId: productId,
        UserId: parseInt(userId),
        Rating: rating,
        Comment: "Product Rating",
      };

      const response = await AddReviewOnProduct(reviewData);
      if (response.status) fetchOrders(searchTerm);
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      setLoadingState((prev) => ({ ...prev, reviewing: null }));
    }
  };

  const handleOrderTracking = async (orderGuid) => {
    navigate("/order-tracking", { state: { orderGuid } });
  };

  return (
    <>
      <div className="main-content">
        <div className="d-flex align-items-center justify-content-between mb_28 order-content-box">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <h2 className="mb-0 fw_600 fs_24 lh-1">Your Orders</h2>
            <div className="drop-down-content d-md-none">
              <select
                className="form-select select-transparent"
                value={dateFilter}
                onChange={handleDateFilterChange}
              >
                {dateFilterOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-input search-field">
            <input
              type="text"
              className="form-control"
              placeholder="Search Your Order"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={search} alt="Search" className="search-icon" />
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between gap-2 py_14 px_32 order-box mb_28">
          <div className="d-flex align-items-center">
            <img src={OrderBoxIcon} alt="Order Icon" className="img-fluid" />
            <h5 className="fs_18 fw_500 text-color lh-1 text-uppercase ml_10">
              {orders.filter(order => order.orderStatus === "Completed").length} Order{orders.filter(order => order.orderStatus === "Completed").length > 1 ? "s" : ""} Delivered
            </h5>
          </div>
          <div className="drop-down-content">
            <select
              className="form-select select-transparent"
              value={dateFilter}
              onChange={handleDateFilterChange}
            >
              {dateFilterOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py_50">
            <h5 className="fs_18 fw_400 text-muted">Fetching your orders...</h5>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py_50">
            <h5 className="fs_20 fw_500 text-muted">No Orders Found</h5>
          </div>
        ) : (
          <div className="order-list-box" style={{ overflowY: "auto", maxHeight: "400px" }}>
            {orders.map((order) => (
              <div
                key={order.orderId}
                className="order-card"
                style={{
                  backgroundColor: order.orderStatus === "Cancelled" ? "#fcc3c9ff" : "",
                }}
              >
                <div className="order-invoice d-flex align-items-center justify-content-between gap-2 mb_30">
                  <div className="d-flex align-items-center delivery-details">
                    <div className="order-name">
                      <p className="fs_14 fw_400 lh-1 text-color mb_10">
                        {order.deliveryDate ? "Order Delivered" : "Expected Delivery"}
                      </p>
                      <h6 className="fs_18 fw_500 lh-1 text-color mb-0">
                        {order.deliveryDate
                          ? order.deliveryDate != null 
                            ? new Date(order.deliveryDate).toLocaleString() : ""
                          : order.expectedDeliveryDateUtc != null
                            ? (() => {
                                const d = new Date(order.expectedDeliveryDateUtc);
                                return `${d.toLocaleDateString()}, 6:00 PM - 9:00 PM`;
                              })()
                            : ""}
                      </h6>
                    </div>

                    <div className="order-name">
                      <p className="fs_14 fw_400 lh-1 text-color mb_10">Total</p>
                      <h6 className="fs_18 fw_500 lh-1 text-color mb-0">{order.totalPrice} INR</h6>
                    </div>

                    <div className="order-name">
                      <p className="fs_14 fw_400 lh-1 text-color mb_10">Ship To</p>
                      <h6 className="fs_18 fw_500 lh-1 text-color mb-0">{order.shippingAddress}</h6>
                    </div>
                  </div>

                  <div className="invoice-download text-end">
                    <p className="fs_14 fw_400 lh-1 text-color mb_10">
                      order # {order.orderGuid}
                    </p>

                    {order.orderStatus === "Pending" && order.paymentStatus === "Paid" && (
                      <h6
                        className="fw_400 fs_16 lh-1 text-decoration-underline text-danger"
                        style={{
                          cursor:
                            loadingState.cancelOrder === order.orderId
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            loadingState.cancelOrder === order.orderId ? 0.6 : 1,
                        }}
                        onClick={() =>
                          loadingState.cancelOrder === order.orderId
                            ? null
                            : handleCancelOrder(order.orderId)
                        }
                      >
                        {loadingState.cancelOrder === order.orderId ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Cancelling...
                          </>
                        ) : (
                          "Cancel Order"
                        )}
                      </h6>
                    )}

                    {order.paymentStatus !== "Paid" && (
                      <h6
                        className="fw_400 fs_16 lh-1 text-primary text-decoration-underline"
                        style={{
                          cursor:
                            loadingState.makePayment === order.orderId
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            loadingState.makePayment === order.orderId ? 0.6 : 1,
                        }}
                        onClick={() =>
                          loadingState.makePayment === order.orderId
                            ? null
                            : handleMakePayment(order)
                        }
                      >
                        {loadingState.makePayment === order.orderId ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Redirecting...
                          </>
                        ) : (
                          "Make Payment"
                        )}
                      </h6>
                    )}

                      <h6
                        className="fw_400 fs_16 lh-1 text-decoration-underline mt-2"
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => handleOrderTracking(order.orderGuid) }
                      >
                        Track Order
                      </h6>

                    {order.orderStatus === "Completed" && (
                      <h6
                        className="fw_400 fs_16 lh-1 text-decoration-underline"
                        style={{
                          cursor:
                            loadingState.download === order.orderId
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            loadingState.download === order.orderId ? 0.6 : 1,
                        }}
                        onClick={() =>
                          loadingState.download === order.orderId
                            ? null
                            : handleDownloadInvoice(order.orderId)
                        }
                      >
                        {loadingState.download === order.orderId ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Downloading...
                          </>
                        ) : (
                          "Download Invoice"
                        )}
                      </h6>
                    )}
                  </div>
                </div>

                <div className="order-grid-list">
                  {order.products.map((product) => (
                    <div
                      key={product.productId}
                      className="cart-grid d-flex align-items-center justify-content-between gap-2"
                    >
                      <div className="d-flex align-items-start">
                        <div className="img-wrapper">
                          <img
                            className="img-fluid"
                            src={getImageUrl(product.image, ProductImage)}
                            onError={(e) => handleImageError(e, ProductImage)}
                            alt="cart product"
                          />
                        </div>
                        <div>
                          <div className="mb_10">
                            <h6 className="fs_24 fw_700 text-color mb-0">
                              {product.productName} {product.weight > 0 && `(${product.weight}KG)`}
                            </h6>
                          </div>
                          <div className="tag-list d-flex align-items-center">
                            {product.attributes.subProducts.map((subProduct) => (
                              <div
                                key={subProduct.id}
                                className="tag py_10 px_10 fs_12 text-color"
                              >
                                {subProduct.name} {subProduct.weight}
                              </div>
                            ))}
                            {product.attributes.attributes.map((attr) => (
                              <div
                                key={attr.id}
                                className="tag py_10 px_10 fs_12 text-color"
                              >
                                {attr.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="fs_18 fw_400 lh-1-5 mb_20 text-color">
                          You Have Rated {product.productRating} Star
                        </p>

                        <div className="d-flex align-items-center gap-2">
                          {[...Array(5)].map((_, index) => (
                            <img
                              key={index}
                              src={
                                index < product.productRating
                                  ? FillStartIcon
                                  : StartIcon
                              }
                              className="img-fluid"
                              alt=""
                              style={{
                                cursor:
                                  loadingState.reviewing === product.productId
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  loadingState.reviewing === product.productId
                                    ? 0.6
                                    : 1,
                              }}
                              onClick={() =>
                                loadingState.reviewing === product.productId
                                  ? null
                                  : openModal(order.orderId, product.productId, index + 1)
                              }
                            />
                          ))}
                          {loadingState.reviewing === product.productId && (
                            <span className="ms-2">
                              <span className="spinner-border spinner-border-sm"></span>
                              Submitting...
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* -------------------------------------------------------------------
          ⭐⭐⭐ BOOTSTRAP MODAL FOR COMMENT + RATING ⭐⭐⭐
      ------------------------------------------------------------------- */}
      <div className="modal fade" ref={modalRef}>
        <div className="modal-dialog">
          <div className="modal-content">

            <div className="modal-header">
              <h5 className="modal-title">Write a Review</h5>
              <button className="btn-close" onClick={closeModal}></button>
            </div>

            <div className="modal-body">
              <h6 className="mb-3">Your Rating: ⭐ {selectedRating}</h6>

              <textarea
                className="form-control"
                rows="4"
                placeholder="Write your comment here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              ></textarea>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Close
              </button>
              <button className="btn btn-primary" onClick={submitReview}>
                Submit Review
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Order;